import { useState, useEffect } from 'react';
import {
    useNavigate,
} from 'react-router-dom';

import { useLoadingScreen } from '../../Store/LoadingManager';

import { useCompanyUsersHook } from '../../Hooks/Companies/companyUsersHooks';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';

import { IconPlus, IconEdit, IconTrash, IconCloudComputing } from '@tabler/icons-react';

import { CompanyDetailsInterface } from '../../Models/Company/Company';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { CompanyUserField, CompanyField, CompanyLicenseField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { TriggerTypeEnum } from '../../Utilities/Enums/CompanyEnum';

import { CompanyUser } from '../../Models/Company/CompanyUser';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';

import AddEditCompanyUserModal from './AddEditCompanyUserModal';

export default function CompanyUsers(props: CompanyDetailsInterface) {
    //Variables
    let { companyid, viewType, setviewType, setopenDelete, setdeleteType, settoDeleteItem, getCompanyData, previewData, setreinitializeData } = props;
    let navigate = useNavigate();
    let { loading } = useLoadingScreen();

    //Use States
    let [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: companyid,
        sortBy: QueryBuilderHelper.DefaultSortValue(CompanyUserField.NAME),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [companyUsersFormData, setcompanyUsersFormData] = useState<CompanyUser>(new CompanyUser());
    let [openAddEditCompanyUserModal, setopenAddEditCompanyUserModal] = useState<boolean>(false);

    let q_queryKey = [CachingEnum.COMPANY_USERS, queryStr]
    let { data, fetchStatus, syncCompanyUser } = useCompanyUsersHook(q_queryKey, queryStr, viewType != ViewType.ADD && !loading);
    
    //Variables
    const tableColumns = [
        {
            accessor: CompanyUserField.NAME,
            sortable: true,
            render: (record: CompanyUser) => record?.[CompanyUserField.NAME] ?? "--",
        },
        {
            accessor: CompanyUserField.EMPLOYEE_CODE,
            title: "Employee Code",
            sortable: true,
            render: (record: CompanyUser) => record?.[CompanyUserField.EMPLOYEE_CODE] ?? "--",
        },
        {
            accessor: CompanyUserField.USERNAME,
            title: 'Username',
            sortable: true,
            render: (record: CompanyUser) => record?.[CompanyUserField.USERNAME] ?? "--",
        },
        {
            accessor: CompanyUserField.DATABASE_ID,
            title: 'Database Id',
            sortable: true,
            render: (record: CompanyUser) => record?.[CompanyUserField.DATABASE_ID] ?? "--",
        },
        {
            accessor: 'action',
            title: "Action",
            hidden: viewType == ViewType.PREVIEW,
            render: (record: CompanyUser) => <>
                <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={(e: any) => {
                        setopenAddEditCompanyUserModal(true);
                        setcompanyUsersFormData({...new CompanyUser(), ...record});
                    }} />

                <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={(e: any) => { 
                        if (setopenDelete) {
                            setopenDelete(true);
                        }

                        if (setdeleteType) {
                            setdeleteType('companyUser');
                        }

                        if(settoDeleteItem) {
                            settoDeleteItem(record);
                        }
                    }} />
            </>
        },
    ];

    //Event Handlers
    const handleSyncCompanyUsers = async () => {
        var syncResponse = await syncCompanyUser(JSON.stringify({triggerType: TriggerTypeEnum.MANUAL}));
        let _success = syncResponse?.isSuccess ?? false;

        if(_success) {
            if(getCompanyData) {
                getCompanyData();
            }
        }
    }

    //Rendering Functions
    const renderActionButtons = () => {
        let isAllowAddUser = (previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY] != null && previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY] != "");

        return <>
            <p className='lastSyncDateTime'>Last Sync: {previewData?.[CompanyField.LAST_SYNC_DATETIME] ? DateHelper.getFormattedDate(previewData?.[CompanyField.LAST_SYNC_DATETIME], "dd/MM/yyyy hh:mm a") : "--"}</p>
            
            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Sync Users'
                startIcon={<IconCloudComputing />}
                onClick={(e: any) => {
                    handleSyncCompanyUsers();
                }} />
            {viewType != ViewType.PREVIEW && <CustomButton
                btnType={isAllowAddUser ? ButtonType.PRIMARY : ButtonType.CANCEL}
                title='Add'
                disabled={!isAllowAddUser}
                startIcon={<IconPlus />}
                onClick={(e: any) => {
                    setopenAddEditCompanyUserModal(true);
                }} />}
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.COMPANY_EDIT) && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Edit'
                startIcon={<IconEdit />}
                onClick={(e: any) => {
                    navigate(`${RouteEnum.COMPANY_DETAILS}?id=${companyid}&view=${ViewType.EDIT}`, { replace: true });
                    if(setviewType) {
                        setviewType(ViewType.EDIT);
                    }
                }} />}
        </>
    }

    const renderAddEditCompanyUserModal = () => {
        return <AddEditCompanyUserModal
            companyid={companyid}
            companyUsersFormData={companyUsersFormData}
            setcompanyUsersFormData={setcompanyUsersFormData}
            openAddEditCompanyUserModal={openAddEditCompanyUserModal}
            setopenAddEditCompanyUserModal={setopenAddEditCompanyUserModal}
            getCompanyData={getCompanyData} />
    }

    return <CustomDetailsCard
        title={"Users"}
        actionButtons={renderActionButtons()} >
            <TableContainer>
                <CustomListComponent
                    columns={tableColumns}
                    fetching={fetchStatus == "fetching"}
                    idAccessor={(record: CompanyUser) => `companyUser${record?.[CompanyUserField.PUBLIC_ID]}`}
                    records={data?.data?.list ?? []}
                    totalRecords={data?.data?.metaData?.totalCount ?? 0}
                    page={queryStr.pageNumber}
                    recordsPerPage={queryStr.pageSize}
                    defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                    onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
                    setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                    onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
            </TableContainer>

            {openAddEditCompanyUserModal && renderAddEditCompanyUserModal()}
        </CustomDetailsCard>
}
